import React, { FC, useEffect, useState } from "react";
import { Cancel } from "@material-ui/icons";
import { Dialog, DialogActions, DialogContent, Typography } from "@material-ui/core";
import { useDispatch, useSelector } from "react-redux";

import BaseStepper from "../../../components/modules/BaseStepper";
import Button from "../../../components/elements/Button";
import CustomSlideCheckBoxOption from "../../../components/CampaignCreation/CustomSlideCheckBoxOption";
import classes from "./EditTemplateModal.module.scss";
import { BaseStep } from "../../../components/modules/BaseStepper/BaseStepper";
import { ReduxApplicationState } from "../../../types/ReduxTypes/ReduxApplicationState";
import { initializeStateWithTemplateData } from "../../../utils/initializeStateWithTemplateData";
import { setCampaignSteps, updateCampaignTemplate } from "../../../redux/reducers/campaignToCreate";
import { stepMap, stepOptions, templateSteps as defaultSteps } from "../../../constants/steps";

type EditTemplateModalProps = {
  isOpen: boolean;
  handleClose: () => void;
  templateToEdit: any;
};

const EditTemplateModal: FC<EditTemplateModalProps> = ({ isOpen, handleClose, templateToEdit }) => {
  const dispatch = useDispatch();

  const advertisers = useSelector((state: ReduxApplicationState) => state.agency.advertisers);

  const [steps, setSteps] = useState<BaseStep[]>(defaultSteps);
  const [isStepperVisible, setIsStepperVisible] = useState(false);
  const [customSlideOptions, setCustomSlideOptions] = useState<Record<string, boolean>>({});

  const toggleStep = (checked: boolean, stepComponent: BaseStep) => {
    setSteps((prev) =>
      checked ? [...prev, stepComponent] : prev.filter((step) => (step as any).label !== stepComponent.label),
    );
  };

  const handleCustomSlideOptionsChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { checked, name } = event.target;

    setCustomSlideOptions((prev) => ({ ...prev, [name]: checked }));

    const step = stepMap[name];
    if (step) {
      toggleStep(checked, step);
    }
  };

  const handleGetStarted = async () => {
    dispatch(setCampaignSteps(steps.map((step) => step.name)));
    setIsStepperVisible(true);
  };

  const handleBack = () => {
    setIsStepperVisible(false);
  };

  const handleCancel = () => {
    handleClose();
    handleBack();
  };

  useEffect(() => {
    const updatedSteps = [...defaultSteps];
    const updatedOptions = Object.fromEntries(stepOptions.map((step) => [step.name, false])) as Record<string, boolean>;

    templateToEdit.templateSteps.forEach((stepKey: any) => {
      if (stepMap[stepKey]) {
        updatedSteps.push(stepMap[stepKey]);
        updatedOptions[stepKey] = true;
      }
    });

    setSteps(updatedSteps);
    setCustomSlideOptions(updatedOptions);

    initializeStateWithTemplateData(templateToEdit, dispatch, advertisers);
  }, [templateToEdit, dispatch, advertisers]);

  return (
    <Dialog
      open={isOpen}
      onClose={handleClose}
      aria-labelledby="Edit Template Modal"
      className={classes.container}
      fullScreen
      disableEnforceFocus
      disableAutoFocus
    >
      <div className={classes.dialogTitle}>
        <Typography variant="h2">Edit Template</Typography>
        <Cancel onClick={handleClose} />
      </div>
      <DialogContent className={classes.dialogContent}>
        {isStepperVisible ? (
          <BaseStepper
            steps={steps}
            handleBack={handleBack}
            lastStepNextLabel="Save"
            campaignAction={updateCampaignTemplate}
            typeOfAction="update"
          />
        ) : (
          <div>
            <Typography variant="h4">Selected steps</Typography>
            <div className={classes.steps}>
              {stepOptions.map((option) => (
                <CustomSlideCheckBoxOption
                  key={option.name}
                  label={option.label}
                  description={option.description}
                  icon={option.icon}
                  checked={customSlideOptions[option.name]}
                  name={option.name}
                  handleChange={handleCustomSlideOptionsChange}
                />
              ))}
            </div>
          </div>
        )}
      </DialogContent>
      <DialogActions className={isStepperVisible ? classes.hide : classes.dialogActions}>
        <Button onClick={handleCancel} variant="outlined">
          Cancel
        </Button>
        <Button loaderSize={25} onClick={handleGetStarted}>
          Continue
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EditTemplateModal;
